import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";
import usePostcodeGroupSingle from "../../data/usePostcodeGroupSingle";
import { IPostcodePrice } from "../../models/PostcodeGroup";
import { Fragment, useState } from "react";
import EditPostcodeAreaPrice from "./EditPostcodeAreaPrice";
import RemovePostcodeAreaPrice from "./RemovePostcodeAreaPrice";
import { Switch } from "@headlessui/react";
import classNames from "../../utils/classNames";
import EditPostcodeAreaEngine from "./EditPostcodeAreaEngine";
import EditPostcodeAreaBody from "./EditPostcodeAreaBody";
import EditPostcodeAreaThresholdPrice from "./EditPostcodeAreaThresholdPrice";
import EditPostcodeAreaActive from "./EditPostcodeAreaActive";
import PostcodeYearRules from "./PostcodeYearRules";
import { districtOutcode } from "../../utils/postcodeDistricts";



export default function PostcodeAreasTable({ postcodePrices, outcode }: { postcodePrices: IPostcodePrice[], outcode: string }) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "postcode", desc: false }]);


  const colHelper = createColumnHelper<IPostcodePrice>();

  const cols = [
    // colHelper.display({
    //   id: "name",
    //   header: "Name",
    //   sortingFn: "auto",
    //   cell: (info) => (
    //     <span className="flex  " >
    //       <span className="ml-4 m-auto">{info.row.original.name}</span>
    //     </span>
    //   )
    // }),
    colHelper.accessor("postcode", {
      header: "Postcode",
      id: "postcode",
      cell: (info) => (<span className="font-medium">{districtOutcode(outcode, info.getValue())}</span>)
    }),
    colHelper.display({
      header: "Price per Tonne (£)",
      cell: (info) => (
        <EditPostcodeAreaPrice postcodeArea={info.row.original} />
      )
    }),
    colHelper.display({
      header: "Minumum Price (£)",
      cell: (info) => (
        <EditPostcodeAreaThresholdPrice postcodeArea={info.row.original} />
      )
    }),
    colHelper.display({
      header: "Engine",
      cell: (info) => (
        <EditPostcodeAreaEngine postcodeArea={info.row.original} />
      )
    }),
    colHelper.display({
      header: "Body",
      cell: (info) => (
        <EditPostcodeAreaBody postcodeArea={info.row.original} />
      )
    }),
    colHelper.display({
      header: "Active",
      cell: (info) => (
        <EditPostcodeAreaActive postcodeArea={info.row.original} />
      )
    }),
    colHelper.display({
      header: "Year Rules",
      cell: (info) => {
        const count = info.row.original.yearRules?.length ?? 0;
        return (
          <button
            type="button"
            onClick={() => info.row.toggleExpanded()}
            className={classNames(
              "inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium",
              count > 0
                ? "border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100"
                : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            )}
          >
            {info.row.getIsExpanded() ? "Hide" : count > 0 ? `${count} rule${count === 1 ? "" : "s"}` : "Add"}
          </button>
        );
      }
    }),
    colHelper.display({
      header: "Delete",
      cell: (info) => (
        <RemovePostcodeAreaPrice postcodeArea={info.row.original} />
      )
    }),
  ];
  


  const table = useReactTable({
    data: postcodePrices,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // Mobile card component
  const MobileCard = ({ postcodePrice }: { postcodePrice: IPostcodePrice }) => {
    const [showYearRules, setShowYearRules] = useState(false);
    const ruleCount = postcodePrice.yearRules?.length ?? 0;

    return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm">
      <div className="flex justify-between items-start mb-3">
        <div className="font-medium text-gray-900">{districtOutcode(outcode, postcodePrice.postcode)}</div>
        <RemovePostcodeAreaPrice postcodeArea={postcodePrice} />
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Price per Tonne:</span>
          <EditPostcodeAreaPrice postcodeArea={postcodePrice} />
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Minimum Price:</span>
          <EditPostcodeAreaThresholdPrice postcodeArea={postcodePrice} />
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Engine Condition:</span>
          <EditPostcodeAreaEngine postcodeArea={postcodePrice} />
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Body Condition:</span>
          <EditPostcodeAreaBody postcodeArea={postcodePrice} />
        </div>

        <div className="border-t border-gray-200 pt-3">
          <button
            type="button"
            onClick={() => setShowYearRules((prev) => !prev)}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            {showYearRules
              ? "Hide year rules"
              : ruleCount > 0
                ? `Year rules (${ruleCount})`
                : "Add year rules"}
          </button>

          {showYearRules && (
            <div className="mt-3">
              <PostcodeYearRules
                compact
                basePrice={postcodePrice.price}
                yearRules={postcodePrice.yearRules ?? []}
                postcodePriceId={postcodePrice.id}
                ownerLabel={`area ${districtOutcode(outcode, postcodePrice.postcode)}`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="mt-4">
      {/* Mobile view - cards */}
      <div className="block sm:hidden">
        <div className="space-y-3">
          {postcodePrices.map((postcodePrice, index) => (
            <MobileCard key={index} postcodePrice={postcodePrice} />
          ))}
        </div>
      </div>

      {/* Desktop view - table */}
      <div className="hidden sm:block bg-white overflow-hidden rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">

            {/* Table headings - very similar to all the other tables */}
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="bg-gp-blue-50 text-gray-800">
                  {headerGroup.headers.map((header, i) => (
                    <th
                      key={header.id}
                      scope="col"
                      className="py-3 px-3 text-sm font-semibold text-left whitespace-nowrap"
                    >
                      {header.isPlaceholder
                        ? null
                        : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: ' ▲',
                              desc: ' ▼',
                            }[header.column.getIsSorted() as string] ?? null}
                          </div>

                        )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            {/* Body */}
            <tbody className="bg-white divide-y divide-gray-200">
              {table.getRowModel().rows.map((row, i) => {
                let rowCells = row.getVisibleCells();
                return (
                  <Fragment key={i}>
                    <tr
                      key={row.id}
                      // onClick={() => setPostcodeGroupPrice(row.original)}
                      className={"hover:bg-gray-50 transition-colors"}
                    >
                      {rowCells.map((cell) => (
                        <td key={cell.id} className="px-3 py-3 text-sm whitespace-nowrap">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {row.getIsExpanded() && (
                      <tr>
                        <td colSpan={row.getVisibleCells().length} className="bg-gray-50 px-3 py-4">
                          <PostcodeYearRules
                            compact
                            basePrice={row.original.price}
                            yearRules={row.original.yearRules ?? []}
                            postcodePriceId={row.original.id}
                            ownerLabel={`area ${districtOutcode(outcode, row.original.postcode)}`}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>

          </table>
        </div>
      </div>
    </div>
  )
}